import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useInteractionPreferences } from '../contexts/InteractionPreferencesContext';
import { haptic } from '../utils/vibration';

export const BOTTOM_SHEET_CLOSE_THRESHOLD = 0.3;
export const BOTTOM_SHEET_DRAG_ZONE_HEIGHT = 80;

const INTERACTIVE_DRAG_EXCLUSION_SELECTOR = [
  'button',
  'a',
  'input',
  'textarea',
  'select',
  '[role="button"]',
  '[contenteditable="true"]',
].join(',');

export const shouldCloseBottomSheet = (offsetY, panelHeight) => (
  Number.isFinite(offsetY)
  && Number.isFinite(panelHeight)
  && panelHeight > 0
  && offsetY >= panelHeight * BOTTOM_SHEET_CLOSE_THRESHOLD
);

export const shouldEnableSwipeToClose = (variant, swipeToClose) => (
  swipeToClose ?? (variant === 'sheet')
);

let openModalsCount = 0;
let originalBodyOverflow = '';
let originalBodyOverscroll = '';

const lockBodyScroll = () => {
  if (typeof document === 'undefined') return;
  if (openModalsCount === 0) {
    originalBodyOverflow = document.body.style.overflow;
    originalBodyOverscroll = document.body.style.overscrollBehaviorY;
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehaviorY = 'none';
  }
  openModalsCount += 1;
};

const unlockBodyScroll = () => {
  if (typeof document === 'undefined') return;
  openModalsCount = Math.max(0, openModalsCount - 1);
  if (openModalsCount === 0) {
    document.body.style.overflow = originalBodyOverflow;
    document.body.style.overscrollBehaviorY = originalBodyOverscroll;
  }
};

const VARIANT_CONFIG = {
  sheet: {
    hasBackdrop: true,
    layout: 'items-end justify-center sm:items-center',
    panel: 'min-h-0 w-full rounded-t-2xl bg-white shadow-xl dark:bg-gray-900 sm:rounded-2xl sm:max-w-lg lg:max-w-xl overscroll-y-contain',
    enterClass: 'translate-y-0 opacity-100 sm:scale-100 sm:translate-y-0',
    exitClass: 'translate-y-full opacity-0 sm:scale-95 sm:translate-y-4',
    transitionClass: 'transition-all duration-250 ease-out',
  },
  window: {
    hasBackdrop: true,
    layout: 'items-end justify-center px-3 pb-4 sm:items-center sm:pb-0',
    panel: 'w-full max-w-md rounded-2xl bg-white p-4 shadow-xl dark:bg-gray-900 overscroll-y-contain',
    enterClass: 'opacity-100 scale-100 translate-y-0',
    exitClass: 'opacity-0 scale-95 translate-y-3 sm:translate-y-2',
    transitionClass: 'transition-all duration-200 ease-out',
  },
  fullscreen: {
    hasBackdrop: false,
    layout: 'items-stretch justify-stretch',
    panel: 'flex min-h-0 flex-1 flex-col bg-black/95 text-white',
    enterClass: 'opacity-100',
    exitClass: 'opacity-0',
    transitionClass: 'transition-opacity duration-180 ease-out',
  },
  page: {
    hasBackdrop: false,
    layout: 'items-stretch justify-stretch w-full max-w-full overflow-hidden',
    panel: 'flex min-h-0 flex-1 flex-col w-full max-w-full overflow-hidden bg-slate-100 text-gray-900 dark:bg-slate-950 dark:text-gray-100',
    enterClass: 'opacity-100',
    exitClass: 'opacity-0',
    transitionClass: 'transition-opacity duration-180 ease-out',
  },
};

const joinClasses = (...values) => values.filter(Boolean).join(' ');

const ModalShell = ({
  open,
  onClose,
  children,
  variant = 'sheet',
  zIndexClass = '',
  containerClassName = '',
  panelClassName = '',
  closeOnOverlay = true,
  swipeToClose,
  expandable = false,
  expandedHeight = '95dvh',
  expanded: externalExpanded,
  onExpandedChange,
  panelProps = {},
  container = null,
}) => {
  const { animationsEnabled } = useInteractionPreferences();
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.sheet;
  const [animatingOut, setAnimatingOut] = useState(false);
  const [active, setActive] = useState(() => Boolean(open && !animationsEnabled));
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isExpanded = externalExpanded !== undefined ? externalExpanded : internalExpanded;
  const setIsExpanded = useCallback((updater) => {
    const next = typeof updater === 'function' ? updater(isExpanded) : updater;
    if (externalExpanded === undefined) {
      setInternalExpanded(next);
    }
    onExpandedChange?.(next);
  }, [externalExpanded, isExpanded, onExpandedChange]);
  const wasOpenRef = useRef(open);
  const openTimeRef = useRef(0);
  const containerRef = useRef(null);
  const panelRef = useRef(null);
  const touchStateRef = useRef({ startY: 0, currentDeltaY: 0, isDragging: false });
  const openRef = useRef(open);
  const onCloseRef = useRef(onClose);
  openRef.current = open;
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) {
      setIsExpanded(false);
    }
  }, [open, setIsExpanded]);

  const canSwipeToClose = (
    shouldEnableSwipeToClose(variant, swipeToClose)
    && typeof onClose === 'function'
  );

  // При open=true активное состояние включается через double-RAF для гладкого перехода (при включенных анимациях).
  // При open=false даем время transition-анимации на выход перед размонтированием (только если модалка реально была открыта).
  useLayoutEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      openTimeRef.current = Date.now();
      setAnimatingOut(false);
      if (!animationsEnabled) {
        setActive(true);
        return undefined;
      }
      let innerRaf = null;
      const outerRaf = requestAnimationFrame(() => {
        innerRaf = requestAnimationFrame(() => {
          setActive(true);
        });
      });
      return () => {
        cancelAnimationFrame(outerRaf);
        if (innerRaf) cancelAnimationFrame(innerRaf);
      };
    }

    // Если модалка и так была закрыта (первый рендер), не запускаем выход
    if (!wasOpenRef.current) {
      setActive(false);
      setAnimatingOut(false);
      return undefined;
    }

    wasOpenRef.current = false;
    setActive(false);
    if (!animationsEnabled) {
      setAnimatingOut(false);
      return undefined;
    }

    setAnimatingOut(true);
    const timer = setTimeout(() => {
      setAnimatingOut(false);
    }, 250);

    return () => clearTimeout(timer);
  }, [open, animationsEnabled]);

  const isMounted = Boolean(open || animatingOut);

  // Блокировка скролла фона и overscroll на body при открытой модалке
  useEffect(() => {
    if (!isMounted) return undefined;
    lockBodyScroll();
    return () => {
      unlockBodyScroll();
    };
  }, [isMounted]);

  // Обработка свайпа закрытия шторки на мобильных
  const handleTouchStart = useCallback((event) => {
    if (!canSwipeToClose || typeof window === 'undefined' || window.innerWidth >= 640) return;
    const target = event.target;
    if (target instanceof Element && target.closest(INTERACTIVE_DRAG_EXCLUSION_SELECTOR)) return;

    const panel = panelRef.current;
    if (!panel) return;
    const panelRect = panel.getBoundingClientRect();
    const touchY = event.touches[0]?.clientY ?? 0;

    const dragZoneHeight = Math.max(BOTTOM_SHEET_DRAG_ZONE_HEIGHT, 100);
    if (touchY < panelRect.top || touchY > panelRect.top + dragZoneHeight) {
      return;
    }

    touchStateRef.current = {
      startY: touchY,
      currentDeltaY: 0,
      isDragging: true,
    };
  }, [canSwipeToClose]);

  const handleTouchMove = useCallback((event) => {
    if (!touchStateRef.current.isDragging) return;

    // Сразу отменяем нативный жест браузера (pull-to-refresh / скролл страницы),
    // пока событие отменяемо и браузер не перехватил управление
    if (event.cancelable) {
      event.preventDefault();
    }

    const touchY = event.touches[0]?.clientY ?? 0;
    const deltaY = touchY - touchStateRef.current.startY;
    touchStateRef.current.currentDeltaY = deltaY;

    const panel = panelRef.current;
    if (!panel) return;

    if (deltaY >= 0) {
      panel.style.transform = `translate3d(0, ${deltaY}px, 0)`;
      panel.style.transition = 'none';
    } else if (expandable && !isExpanded && deltaY < 0) {
      // Свайп вверх для раскрытия: визуальная пружинящая отдача
      const pullUpOffset = Math.max(-40, deltaY * 0.35);
      panel.style.transform = `translate3d(0, ${pullUpOffset}px, 0)`;
      panel.style.transition = 'none';
    }
  }, [expandable, isExpanded]);

  const handleTouchEnd = useCallback(() => {
    if (!touchStateRef.current.isDragging) return;
    touchStateRef.current.isDragging = false;
    const deltaY = touchStateRef.current.currentDeltaY;
    const panel = panelRef.current;

    if (panel) {
      panel.style.transition = '';
      panel.style.transform = '';
    }

    // 1. Свайп вверх: развернуть шторку в полное состояние
    if (expandable && !isExpanded && deltaY < -40) {
      haptic('selection');
      setIsExpanded(true);
      return;
    }

    const panelHeight = panel?.getBoundingClientRect().height || 0;

    // 2. Если шторка уже была развернута: свайп вниз возвращает её в исходное (свёрнутое) состояние
    if (expandable && isExpanded) {
      if (deltaY > 40) {
        haptic('selection');
        setIsExpanded(false);
      }
      return;
    }

    // 3. Обычный свайп вниз для закрытия
    if (shouldCloseBottomSheet(deltaY, panelHeight)) {
      if (panel) {
        panel.style.transform = 'translate3d(0, 100%, 0)';
      }
      haptic('close');
      onCloseRef.current?.();
    } else {
      if (panel) {
        panel.style.transform = '';
      }
    }
  }, [expandable, isExpanded, setIsExpanded]);

  // Привязываем touchmove напрямую с { passive: false }, чтобы гарантировать отмену pull-to-refresh
  useEffect(() => {
    if (!isMounted) return undefined;
    const panel = panelRef.current;
    if (!panel || !canSwipeToClose) return undefined;

    const onNativeTouchMove = (event) => {
      handleTouchMove(event);
    };

    panel.addEventListener('touchmove', onNativeTouchMove, { passive: false });
    return () => {
      panel.removeEventListener('touchmove', onNativeTouchMove);
    };
  }, [isMounted, canSwipeToClose, handleTouchMove]);

  if (!isMounted || typeof document === 'undefined') return null;

  const defaultZIndex = (variant === 'fullscreen' || variant === 'page') ? 'z-[80]' : 'z-50';
  const { onClick: panelOnClick, style: panelPropStyle = {}, ...restPanelProps } = panelProps;

  const panelStyle = {
    ...panelPropStyle,
    ...(isExpanded ? {
      maxHeight: expandedHeight,
      height: expandedHeight,
    } : {}),
  };

  const portalTarget = container || (typeof document !== 'undefined' ? document.body : null);
  if (!portalTarget) return null;

  return ReactDOM.createPortal(
    <div
      ref={containerRef}
      data-modal-shell="true"
      className={joinClasses(
        container ? 'absolute inset-0 select-none overscroll-y-contain' : 'fixed inset-0 select-none overscroll-y-contain',
        'overflow-hidden max-w-full',
        zIndexClass || defaultZIndex
      )}
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      {/* Затемнение (backdrop) */}
      {config.hasBackdrop ? (
        <div
          className={joinClasses(
            container ? 'absolute inset-0 bg-black/50 pointer-events-auto' : 'fixed inset-0 bg-black/50 pointer-events-auto',
            animationsEnabled ? 'transition-opacity duration-200 ease-out' : 'transition-none',
            active ? 'opacity-100' : 'opacity-0'
          )}
          onClick={(event) => {
            if (!active || !closeOnOverlay) return;
            // Защита от фантомных кликов (ghost clicks) с тача при открытии
            if (Date.now() - openTimeRef.current < 350) return;
            haptic('close');
            onClose?.(event);
          }}
          aria-hidden="true"
        />
      ) : null}

      {/* Обертка выравнивания */}
      <div
        className={joinClasses(
          container ? 'absolute inset-0 flex pointer-events-none' : 'fixed inset-0 flex pointer-events-none',
          config.layout,
          containerClassName
        )}
      >
        {/* Панель модального окна */}
        <div
          ref={panelRef}
          {...restPanelProps}
          className={joinClasses(
            'pointer-events-auto',
            config.panel,
            animationsEnabled ? config.transitionClass : 'transition-none',
            active ? config.enterClass : config.exitClass,
            isExpanded ? '!max-h-[95dvh] !h-[95dvh]' : '',
            panelClassName
          )}
          style={panelStyle}
          onClick={(event) => {
            event.stopPropagation();
            panelOnClick?.(event);
          }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
        >
          {/* Индикатор свайпа для шторок на мобильных */}
          {canSwipeToClose ? (
            <div
              className={joinClasses(
                'relative z-10 h-7 w-full shrink-0 select-none touch-none sm:hidden',
                expandable ? 'cursor-pointer' : ''
              )}
              aria-hidden="true"
              onClick={expandable ? () => {
                haptic('selection');
                setIsExpanded((prev) => !prev);
              } : undefined}
              title={expandable ? (isExpanded ? 'Свернуть' : 'Развернуть') : undefined}
            >
              <span
                className={joinClasses(
                  'pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gray-300 dark:bg-gray-600 transition-all duration-200',
                  isExpanded ? 'h-1.5 w-16' : 'h-1.5 w-12'
                )}
              />
            </div>
          ) : null}

          {children}
        </div>
      </div>
    </div>,
    portalTarget
  );
};

export default ModalShell;
